import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { openDB, IDBPDatabase } from 'idb';
import { FileContent } from '../interfaces/file-system.interface';
import { ChatMessage } from '../interfaces/chat-message.interface';
import { DictContentStructure } from '../interfaces/ai-content.interface';

@Injectable({
  providedIn: 'root'
})
export class IndexedDBService {
  private db: IDBPDatabase | null = null;
  private readonly DB_NAME = 'OttoWebsiteFiles';
  private readonly DB_VERSION = 2;
  private readonly STORE_NAME = 'websiteFiles';
  private readonly CHAT_STORE = 'chatMessages';
  private readonly USER_PREFS_KEY = 'userPreferences';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initDB();
    }
  }

  /**
   * Perform startup cleanup of legacy keys
   * This should be called during application initialization
   */
  async performStartupCleanup(): Promise<void> {
    try {
      console.log('IndexedDB: Performing startup cleanup...');
      
      // Wait for DB to be initialized
      if (!this.db) await this.initDB();
      if (!this.db) return;
      
      // Clean up legacy user preference keys
      await this.cleanupLegacyUserPreferenceKeys();
      
      console.log('IndexedDB: Startup cleanup completed');
    } catch (error) {
      console.error('IndexedDB: Failed to perform startup cleanup:', error);
    }
  }

  /**
   * Extract company GUID from any encoded ID format (legacy or new)
   * This is a helper method to ensure consistent GUID extraction
   */
  private extractCompanyGuid(userId: string): string | null {
    try {
      // If userId contains legacy format indicators, extract just the GUID
      if (userId.includes(':')) {
        const parts = userId.split(':');
        if (parts.length === 3 && parts[0] === 'otto-website-builder-2024') {
          // Legacy format: salt:guid:timestamp
          return parts[1]; // Return just the GUID
        }
      }
      
      // New format: just the GUID
      return userId;
    } catch (error) {
      console.error('IndexedDB: Failed to extract company GUID:', error);
      return null;
    }
  }

  private async initDB(): Promise<void> {
    try {
      this.db = await openDB(this.DB_NAME, this.DB_VERSION, {
        upgrade(db, oldVersion) {
          // Create object store for website files if not exists
          if (!db.objectStoreNames.contains('websiteFiles')) {
            const store = db.createObjectStore('websiteFiles', { keyPath: 'path' });
            store.createIndex('template', 'template');
            store.createIndex('lastModified', 'lastModified');
          }

          // Create object store for metadata if not exists
          if (!db.objectStoreNames.contains('metadata')) {
            db.createObjectStore('metadata', { keyPath: 'key' });
          }

          // Create object store for chat messages if not exists
          if (!db.objectStoreNames.contains('chatMessages')) {
            const chatStore = db.createObjectStore('chatMessages', { keyPath: 'id', autoIncrement: true });
            chatStore.createIndex('userId', 'userId');
            chatStore.createIndex('timestamp', 'timestamp');
          }
        },
      });
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error);
    }
  }

  private sanitizeDataForStorage(data: any): any {
    if (data === null || data === undefined) {
      return data;
    }
    
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      return data;
    }
    
    if (data instanceof Date) {
      return data.toISOString();
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeDataForStorage(item));
    }
    
    if (typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        // Skip functions and other non-serializable data
        if (typeof value !== 'function' && value !== undefined) {
          try {
            sanitized[key] = this.sanitizeDataForStorage(value);
          } catch (error) {
            console.warn(`Failed to sanitize property ${key}:`, error);
            // Skip this property if it can't be sanitized
          }
        }
      }
      return sanitized;
    }
    
    // For any other type, return null to avoid serialization issues
    return null;
  }

  async storeFile(file: FileContent, template: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) throw new Error('IndexedDB not available');

    try {
      const fileRecord = {
        path: file.path,
        content: file.content,
        mimeType: file.mimeType,
        lastModified: file.lastModified,
        template: template
      };

      await this.db.put(this.STORE_NAME, fileRecord);
    } catch (error) {
      console.error('Failed to store file:', error);
      throw error;
    }
  }

  async storeFiles(files: Map<string, FileContent>, template: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) throw new Error('IndexedDB not available');

    try {
      const tx = this.db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);

      // Clear existing files for this template
      const index = store.index('template');
      const existingKeys = await index.getAllKeys(template);
      
      for (const key of existingKeys) {
        await store.delete(key);
      }

      // Store new files
      for (const [path, file] of files) {
        const fileRecord = {
          path: file.path,
          content: file.content,
          mimeType: file.mimeType,
          lastModified: file.lastModified,
          template: template
        };
        await store.put(fileRecord);
      }

      await tx.done;
    } catch (error) {
      console.error('Failed to store files:', error);
      throw error;
    }
  }

  async getFile(path: string): Promise<FileContent | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    if (!this.db) await this.initDB();
    if (!this.db) return null;

    try {
      const record = await this.db.get(this.STORE_NAME, path);
      if (!record) return null;

      return {
        path: record.path,
        content: record.content,
        mimeType: record.mimeType,
        lastModified: new Date(record.lastModified)
      };
    } catch (error) {
      console.error('Failed to get file:', error);
      return null;
    }
  }

  async getFilesByTemplate(template: number): Promise<Map<string, FileContent>> {
    if (!isPlatformBrowser(this.platformId)) return new Map();
    if (!this.db) await this.initDB();
    if (!this.db) return new Map();

    try {
      const index = this.db.transaction(this.STORE_NAME).objectStore(this.STORE_NAME).index('template');
      const records = await index.getAll(template);

      const files = new Map<string, FileContent>();
      
      for (const record of records) {
        files.set(record.path, {
          path: record.path,
          content: record.content,
          mimeType: record.mimeType,
          lastModified: new Date(record.lastModified)
        });
      }

      return files;
    } catch (error) {
      console.error('Failed to get files by template:', error);
      return new Map();
    }
  }

  async deleteTemplate(template: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) return;

    try {
      const tx = this.db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index('template');
      
      const keys = await index.getAllKeys(template);
      
      for (const key of keys) {
        await store.delete(key);
      }

      await tx.done;
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  }

  async storeMetadata(key: string, value: any): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) throw new Error('IndexedDB not available');

    try {
      // Sanitize the data before storing
      const sanitizedValue = this.sanitizeDataForStorage(value);
      const sanitizedData = this.sanitizeDataForStorage({ key, value: sanitizedValue, lastModified: new Date() });
      
      await this.db.put('metadata', sanitizedData);
    } catch (error) {
      console.error('Failed to store metadata:', error);
      throw error;
    }
  }

  async getMetadata(key: string): Promise<any> {
    if (!isPlatformBrowser(this.platformId)) return null;
    if (!this.db) await this.initDB();
    if (!this.db) return null;

    try {
      const record = await this.db.get('metadata', key);
      return record ? record.value : null;
    } catch (error) {
      console.error('Failed to get metadata:', error);
      return null;
    }
  }

  async deleteMetadata(key: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) return;

    try {
      await this.db.delete('metadata', key);
    } catch (error) {
      console.error('Failed to delete metadata:', error);
      throw error;
    }
  }

  async clearAll(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) return;

    try {
      const tx = this.db.transaction([this.STORE_NAME, 'metadata', this.CHAT_STORE], 'readwrite');
      await tx.objectStore(this.STORE_NAME).clear();
      await tx.objectStore('metadata').clear();
      await tx.objectStore(this.CHAT_STORE).clear();
      await tx.done;
      console.log('✅ All IndexedDB data cleared successfully');
    } catch (error) {
      console.error('Failed to clear all data:', error);
      throw error;
    }
  }

  // New methods for chat message management
  async storeChatMessage(userId: string, message: ChatMessage): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) throw new Error('IndexedDB not available');

    try {
      await this.db.add(this.CHAT_STORE, {
        userId,
        content: message.content,
        isBot: message.isBot,
        timestamp: message.timestamp,
      });
    } catch (error) {
      console.error('Failed to store chat message:', error);
      throw error;
    }
  }

  async getChatMessages(userId: string): Promise<ChatMessage[]> {
    if (!isPlatformBrowser(this.platformId)) return [];
    if (!this.db) await this.initDB();
    if (!this.db) return [];

    try {
      const tx = this.db.transaction(this.CHAT_STORE, 'readonly');
      const index = tx.store.index('userId');
      const messages = await index.getAll(userId);
      
      return messages.map(msg => ({
        id: msg.id.toString(),
        content: msg.content,
        isBot: msg.isBot,
        timestamp: new Date(msg.timestamp)
      }));
    } catch (error) {
      console.error('Failed to get chat messages:', error);
      return [];
    }
  }

  async clearChatMessages(userId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.db) await this.initDB();
    if (!this.db) return;

    try {
      const tx = this.db.transaction(this.CHAT_STORE, 'readwrite');
      const index = tx.store.index('userId');
      const keys = await index.getAllKeys(userId);
      
      for (const key of keys) {
        await tx.store.delete(key);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Failed to clear chat messages:', error);
      throw error;
    }
  }

  // User preferences and supplier data management
  async getUserTheme(userId: string): Promise<string | null> {
    try {
      const prefs = await this.getUserPreferencesWithMigration(userId);
      return prefs?.theme || null;
    } catch (error) {
      console.error('Failed to get user theme:', error);
      return null;
    }
  }

  async getUserCustomThemeColor(userId: string): Promise<string | null> {
    try {
      const prefs = await this.getUserPreferencesWithMigration(userId);
      return prefs?.customThemeColor || null;
    } catch (error) {
      console.error('Failed to get user custom theme color:', error);
      return null;
    }
  }

  async setUserTheme(userId: string, theme: string): Promise<void> {
    try {
      // Ensure we're using the standardized userId (just the GUID)
      const standardizedUserId = this.extractCompanyGuid(userId) || userId;
      
      const existingPrefs = await this.getUserPreferencesWithMigration(standardizedUserId) || {};
      const updatedPrefs = { ...existingPrefs, theme };
      await this.storeMetadata(`${this.USER_PREFS_KEY}_${standardizedUserId}`, updatedPrefs);
    } catch (error) {
      console.error('Failed to set user theme:', error);
      throw error;
    }
  }

  async setUserCustomThemeColor(userId: string, color: string): Promise<void> {
    try {
      // Ensure we're using the standardized userId (just the GUID)
      const standardizedUserId = this.extractCompanyGuid(userId) || userId;
      
      const existingPrefs = await this.getUserPreferencesWithMigration(standardizedUserId) || {};
      const updatedPrefs = { ...existingPrefs, customThemeColor: color };
      await this.storeMetadata(`${this.USER_PREFS_KEY}_${standardizedUserId}`, updatedPrefs);
    } catch (error) {
      console.error('Failed to set user custom theme color:', error);
      throw error;
    }
  }

  /**
   * Migrate data from legacy encoded user preference keys to new format
   * This handles the transition from "userPreferences_salt:guid:timestamp" to "userPreferences_guid"
   */
  async migrateLegacyUserPreferences(legacyUserId: string, newUserId: string): Promise<void> {
    try {
      console.log('IndexedDB: Starting migration from legacy key to new key');
      console.log('IndexedDB: Legacy key:', `${this.USER_PREFS_KEY}_${legacyUserId}`);
      console.log('IndexedDB: New key:', `${this.USER_PREFS_KEY}_${newUserId}`);
      
      // Get data from legacy key
      const legacyData = await this.getMetadata(`${this.USER_PREFS_KEY}_${legacyUserId}`);
      
      if (legacyData) {
        console.log('IndexedDB: Found legacy data, migrating...');
        
        // Store data under new key
        await this.storeMetadata(`${this.USER_PREFS_KEY}_${newUserId}`, legacyData);
        
        // Delete legacy key
        await this.deleteMetadata(`${this.USER_PREFS_KEY}_${legacyUserId}`);
        
        console.log('IndexedDB: Migration completed successfully');
      } else {
        console.log('IndexedDB: No legacy data found, skipping migration');
      }
    } catch (error) {
      console.error('IndexedDB: Failed to migrate legacy user preferences:', error);
      throw error;
    }
  }

  /**
   * Get user preferences with automatic migration from legacy format
   * This ensures backward compatibility while standardizing on the new key format
   */
  async getUserPreferencesWithMigration(userId: string): Promise<any | null> {
    try {
      // First try to get data with the current userId
      let prefs = await this.getMetadata(`${this.USER_PREFS_KEY}_${userId}`);
      
      // If no data found and userId contains legacy format indicators, try migration
      if (!prefs && userId.includes(':')) {
        console.log('IndexedDB: Detected potential legacy format, attempting migration...');
        
        // Extract just the GUID part from legacy format
        const guidMatch = userId.match(/([^:]+)$/);
        if (guidMatch) {
          const extractedGuid = guidMatch[1];
          console.log('IndexedDB: Extracted GUID from legacy format:', extractedGuid);
          
          // Try to get data with extracted GUID
          prefs = await this.getMetadata(`${this.USER_PREFS_KEY}_${extractedGuid}`);
          
          if (prefs) {
            console.log('IndexedDB: Found data with extracted GUID, migrating...');
            // Migrate the data
            await this.migrateLegacyUserPreferences(userId, extractedGuid);
            // Update userId to use extracted GUID for future operations
            return prefs;
          }
        }
      }
      
      return prefs;
    } catch (error) {
      console.error('IndexedDB: Failed to get user preferences with migration:', error);
      return null;
    }
  }

  async getUserSupplierData(userId: string): Promise<any | null> {
    try {
      const prefs = await this.getUserPreferencesWithMigration(userId);
      return prefs?.supplierData || null;
    } catch (error) {
      console.error('Failed to get user supplier data:', error);
      return null;
    }
  }

  async setUserSupplierData(userId: string, supplierData: any): Promise<void> {
    try {
      // Ensure we're using the standardized userId (just the GUID)
      const standardizedUserId = this.extractCompanyGuid(userId) || userId;
      
      const existingPrefs = await this.getUserPreferencesWithMigration(standardizedUserId) || {};
      const updatedPrefs = { ...existingPrefs, supplierData };
      await this.storeMetadata(`${this.USER_PREFS_KEY}_${standardizedUserId}`, updatedPrefs);
    } catch (error) {
      console.error('Failed to set user supplier data:', error);
      throw error;
    }
  }

  // New methods for comprehensive state persistence
  async storeAIContent(userId: string, aiContent: any): Promise<void> {
    try {
      await this.storeMetadata(`aiContent_${userId}`, {
        ...aiContent,
        lastModified: new Date()
      });
      console.log('✅ AI content stored to IndexedDB');
    } catch (error) {
      console.error('Failed to store AI content:', error);
      throw error;
    }
  }

  async getAIContent(userId: string): Promise<any | null> {
    try {
      const aiContent = await this.getMetadata(`aiContent_${userId}`);
      return aiContent;
    } catch (error) {
      console.error('Failed to get AI content:', error);
      return null;
    }
  }

  async storeApplicationState(userId: string, state: any): Promise<void> {
    try {
      await this.storeMetadata(`appState_${userId}`, {
        ...state,
        lastModified: new Date()
      });
      console.log('✅ Application state stored to IndexedDB');
    } catch (error) {
      console.error('Failed to store application state:', error);
      throw error;
    }
  }

  async getApplicationState(userId: string): Promise<any | null> {
    try {
      const state = await this.getMetadata(`appState_${userId}`);
      return state;
    } catch (error) {
      console.error('Failed to get application state:', error);
      return null;
    }
  }



  async storeCurrentPhase(userId: string, phase: string): Promise<void> {
    try {
      await this.storeMetadata(`currentPhase_${userId}`, {
        phase,
        lastModified: new Date()
      });
    } catch (error) {
      console.error('Failed to store current phase:', error);
      throw error;
    }
  }

  async getCurrentPhase(userId: string): Promise<string | null> {
    try {
      const phaseData = await this.getMetadata(`currentPhase_${userId}`);
      return phaseData?.phase || null;
    } catch (error) {
      console.error('Failed to get current phase:', error);
      return null;
    }
  }

  async storeEditMode(userId: string, editMode: boolean): Promise<void> {
    try {
      await this.storeMetadata(`editMode_${userId}`, {
        editMode,
        lastModified: new Date()
      });
    } catch (error) {
      console.error('Failed to store edit mode:', error);
      throw error;
    }
  }

  async getEditMode(userId: string): Promise<boolean | null> {
    try {
      const editModeData = await this.getMetadata(`editMode_${userId}`);
      return editModeData?.editMode ?? null;
    } catch (error) {
      console.error('Failed to get edit mode:', error);
      return null;
    }
  }

  async storeTagReplacements(userId: string, tagReplacements: any[]): Promise<void> {
    try {
      await this.storeMetadata(`tagReplacements_${userId}`, {
        tagReplacements,
        lastModified: new Date()
      });
      console.log('✅ Tag replacements stored to IndexedDB');
    } catch (error) {
      console.error('Failed to store tag replacements:', error);
      throw error;
    }
  }

  /**
   * Merge new tag replacements with existing ones instead of overwriting
   * This ensures that all generated tags are preserved across multiple content generation sessions
   * @param userId - The user/company GUID
   * @param newTagReplacements - New tag replacements to merge
   * @param mergeStrategy - How to handle conflicts: 'newer' (default) or 'preserve_existing'
   */
  async mergeTagReplacements(userId: string, newTagReplacements: any[], mergeStrategy: 'newer' | 'preserve_existing' = 'newer'): Promise<void> {
    try {
      // Get existing tag replacements
      const existingReplacements = await this.getTagReplacements(userId) || [];
      
      // Create a map of existing replacements for quick lookup
      const existingMap = new Map();
      existingReplacements.forEach(replacement => {
        existingMap.set(replacement.tagName, replacement);
      });
      
      // Merge new replacements with existing ones
      const mergedReplacements: any[] = [];
      
      // First, add all existing replacements
      existingReplacements.forEach(replacement => {
        mergedReplacements.push({ ...replacement });
      });
      
      // Then, add or update with new replacements
      newTagReplacements.forEach(newReplacement => {
        const existingIndex = mergedReplacements.findIndex(r => r.tagName === newReplacement.tagName);
        
        if (existingIndex >= 0) {
          // Tag already exists - apply merge strategy
          if (mergeStrategy === 'newer') {
            // Replace with newer content
            mergedReplacements[existingIndex] = { ...newReplacement };
            console.log(`🔄 Updated existing tag replacement: ${newReplacement.tagName}`);
          }
          // If mergeStrategy is 'preserve_existing', we don't update
        } else {
          // New tag - add it
          mergedReplacements.push({ ...newReplacement });
          console.log(`✅ Added new tag replacement: ${newReplacement.tagName}`);
        }
      });
      
      // Store the merged replacements
      await this.storeMetadata(`tagReplacements_${userId}`, {
        tagReplacements: mergedReplacements,
        lastModified: new Date()
      });
      
      console.log(`✅ Merged tag replacements stored to IndexedDB: ${existingReplacements.length} existing + ${newTagReplacements.length} new = ${mergedReplacements.length} total`);
      
      // Log detailed information about what was merged
      const newTags = newTagReplacements.filter(newTag => 
        !existingReplacements.some(existingTag => existingTag.tagName === newTag.tagName)
      );
      const updatedTags = newTagReplacements.filter(newTag => 
        existingReplacements.some(existingTag => existingTag.tagName === newTag.tagName)
      );
      
      if (newTags.length > 0) {
        console.log(`📝 New tags added: ${newTags.map(t => t.tagName).join(', ')}`);
      }
      if (updatedTags.length > 0) {
        console.log(`🔄 Tags updated: ${updatedTags.map(t => t.tagName).join(', ')}`);
      }
      
    } catch (error) {
      console.error('Failed to merge tag replacements:', error);
      throw error;
    }
  }

  async getTagReplacements(userId: string): Promise<any[] | null> {
    try {
      const data = await this.getMetadata(`tagReplacements_${userId}`);
      return data?.tagReplacements || null;
    } catch (error) {
      console.error('Failed to get tag replacements:', error);
      return null;
    }
  }

  async storeAllUserData(userId: string, userData: any): Promise<void> {
    try {
      // Store all user data in a single operation
      const dataToStore = {
        ...userData,
        lastModified: new Date()
      };
      
      await this.storeMetadata(`allUserData_${userId}`, dataToStore);
      console.log('✅ All user data stored to IndexedDB');
    } catch (error) {
      console.error('Failed to store all user data:', error);
      throw error;
    }
  }

  async getAllUserData(userId: string): Promise<any | null> {
    try {
      const userData = await this.getMetadata(`allUserData_${userId}`);
      return userData;
    } catch (error) {
      console.error('Failed to get all user data:', error);
      return null;
    }
  }

  async clearUserData(userId: string): Promise<void> {
    try {
      // Clear all user-specific data
      const keysToDelete = [
        `${this.USER_PREFS_KEY}_${userId}`,
        `aiContent_${userId}`,
        `appState_${userId}`,
        `currentPhase_${userId}`,
        `editMode_${userId}`,
        `tagReplacements_${userId}`,
        `allUserData_${userId}`,

      ];

      for (const key of keysToDelete) {
        await this.deleteMetadata(key);
      }

      // Clear chat messages
      await this.clearChatMessages(userId);
      
      console.log('✅ All user data cleared from IndexedDB');
    } catch (error) {
      console.error('Failed to clear user data:', error);
      throw error;
    }
  }

  /**
   * Clean up legacy user preference keys from IndexedDB
   * This removes any keys that contain the old salt:guid:timestamp format
   */
  async cleanupLegacyUserPreferenceKeys(): Promise<void> {
    try {
      console.log('IndexedDB: Starting cleanup of legacy user preference keys...');
      
      if (!this.db) await this.initDB();
      if (!this.db) return;

      const tx = this.db.transaction('metadata', 'readonly');
      const store = tx.objectStore('metadata');
      const allKeys = await store.getAllKeys();
      
      const legacyKeys = allKeys.filter(key => {
        const keyStr = key.toString();
        return keyStr.startsWith(`${this.USER_PREFS_KEY}_`) && keyStr.includes('otto-website-builder-2024:');
      });
      
      console.log('IndexedDB: Found legacy keys to clean up:', legacyKeys);
      
      if (legacyKeys.length > 0) {
        const writeTx = this.db.transaction('metadata', 'readwrite');
        const writeStore = writeTx.objectStore('metadata');
        
        for (const key of legacyKeys) {
          await writeStore.delete(key);
          console.log('IndexedDB: Deleted legacy key:', key);
        }
        
        await writeTx.done;
        console.log('IndexedDB: Legacy key cleanup completed');
      } else {
        console.log('IndexedDB: No legacy keys found to clean up');
      }
    } catch (error) {
      console.error('IndexedDB: Failed to cleanup legacy keys:', error);
      throw error;
    }
  }

  /**
   * Store dict content in IndexedDB
   */
  async storeDictContent(userId: string, dictContent: DictContentStructure): Promise<void> {
    try {
      if (!this.db) await this.initDB();
      if (!this.db) return;
      
      const key = `dictContent_${userId}`;
      await this.db.put('metadata', {
        key,
        value: this.sanitizeDataForStorage(dictContent),
        timestamp: Date.now()
      });
      
      console.log('IndexedDB: Stored dict content for user:', userId);
    } catch (error) {
      console.error('IndexedDB: Failed to store dict content:', error);
      throw error;
    }
  }

  /**
   * Get dict content from IndexedDB
   */
  async getDictContent(userId: string): Promise<DictContentStructure | null> {
    try {
      if (!this.db) await this.initDB();
      if (!this.db) return null;
      
      const key = `dictContent_${userId}`;
      const result = await this.db.get('metadata', key);
      
      if (result?.value) {
        console.log('IndexedDB: Retrieved dict content for user:', userId);
        return result.value as DictContentStructure;
      }
      
      return null;
    } catch (error) {
      console.error('IndexedDB: Failed to get dict content:', error);
      return null;
    }
  }

  /**
   * Clear dict content for user
   */
  async clearDictContent(userId: string): Promise<void> {
    try {
      if (!this.db) await this.initDB();
      if (!this.db) return;
      
      const key = `dictContent_${userId}`;
      await this.db.delete('metadata', key);
      
      console.log('IndexedDB: Cleared dict content for user:', userId);
    } catch (error) {
      console.error('IndexedDB: Failed to clear dict content:', error);
      throw error;
    }
  }

  /**
   * Prepare data for DynamoDB by fetching capabilities and equipment items from IndexedDB
   */
  async prepareDataForDynamo(companyId: string): Promise<{ capabilities: string[], equipmentTitles: string[] } | null> {
    try {
      if (!this.db) await this.initDB();
      if (!this.db) return null;
      
      const result: { capabilities: string[], equipmentTitles: string[] } = {
        capabilities: [],
        equipmentTitles: []
      };
      
      // Fetch capabilities from v2_completeFormData
      const storageKey = `v2_completeFormData_${companyId}`;
      const formData = await this.getMetadata(storageKey);
      
      if (formData && formData.capabilities && Array.isArray(formData.capabilities)) {
        result.capabilities = formData.capabilities;
      } else {
        console.log('IndexedDB: No capabilities found for company:', companyId);
      }
      
      // Fetch equipment items from dictContent
      const dictContent = await this.getDictContent(companyId);
      if (dictContent && dictContent.EquipmentSection && dictContent.EquipmentSection.equipmentItems) {
        const equipmentItems = dictContent.EquipmentSection.equipmentItems;
        result.equipmentTitles = equipmentItems.map((item, index) => {
          const value = item['title'] || item['TITLE'] || Object.values(item)[0] || `Equipment ${index + 1}`;
          return value;
        });
      } else {
        console.log('IndexedDB: No equipment items found for company:', companyId);
      }
      
      // Return result only if we have at least some data
      if (result.capabilities.length > 0 || result.equipmentTitles.length > 0) {
        return result;
      }
      console.log('IndexedDB: No data found for DynamoDB for company:', companyId);
      return null;
    } catch (error) {
      console.error('IndexedDB: Failed to prepare data for DynamoDB:', error);
      return null;
    }
  }


} 